import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Eye, EyeOff } from "lucide-react";
import { generateLegalDoc, generateLegalDocPdf, today, type DocSection } from "./legalDocUtils";
import { buildEvictionSections, minNotice, noticePeriodProblem, NOTICE_TYPES, statesWithKnownMinimums, US_STATES, type NoticeType } from "./legalClauses";
import { positiveAmountProblem } from "./legalValidation";
import { LegalDocPreview } from "./LegalDocPreview";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Generates a general eviction notice template for landlords, as a starting point for the required notice before pursuing formal eviction proceedings.`,
  how: `Fill in the tenant, property, and reason for the notice. Preview the document, then download it as a Word or PDF file.`,
  tip: `Eviction procedures and required notice periods are heavily regulated and vary by state and city \u2014 check your specific local legal requirements carefully, since an improperly issued notice can delay or invalidate the whole process.`,
};

const TITLE = "Notice to Tenant";

export default function EvictionNoticeGenerator() {
  const [landlord, setLandlord] = useState("");
  const [tenant, setTenant] = useState("");
  const [address, setAddress] = useState("");
  const [noticeType, setNoticeType] = useState<NoticeType>("pay-or-quit");
  const [amountOwed, setAmountOwed] = useState("");
  const [violation, setViolation] = useState("");
  const [daysToComply, setDaysToComply] = useState("5");
  const [state, setState] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  const days = Number(daysToComply);
  const periodProblem = daysToComply.trim() ? noticePeriodProblem(state, noticeType, days) : null;
  const known = minNotice(state, noticeType);

  // Choosing a state raises the period to that state's minimum instead of leaving a too-short default.
  const chooseState = (st: string) => {
    setState(st);
    const m = minNotice(st, noticeType);
    if (m && (!(days >= m.days))) setDaysToComply(String(m.days));
  };
  const chooseType = (t: NoticeType) => {
    setNoticeType(t);
    const m = minNotice(state, t);
    if (m && !(days >= m.days)) setDaysToComply(String(m.days));
  };

  const amountProblem = noticeType === "pay-or-quit" ? positiveAmountProblem(amountOwed, "Amount owed") : null;
  const missingRequired = !!amountProblem || !landlord.trim() || !tenant.trim() || !address.trim() || !state.trim() || !daysToComply.trim() || !!periodProblem
    || (noticeType === "pay-or-quit" && !amountOwed.trim()) || (noticeType === "cure-or-quit" && !violation.trim());

  const buildSections = (): DocSection[] => buildEvictionSections({ landlord, tenant, address, type: noticeType, amountOwed, days, violation, dateText: today(), state });

  // Was useLogToolUsage(true, 3000, { ownActions: true }) -- a debounced logger with no
  // gating at all, while the real value-delivering actions (the Word/
  // PDF download buttons below) had ZERO usage-limit check of any
  // kind, not even the weak kind -- confirmed directly: clicking
  // either button at the daily limit generated and downloaded a real,
  // complete document. logUsageNow() gates each button's own click
  // directly, same pattern as Merge PDF's own discrete action.
  const { logUsageNow } = useLogUsageOnAction();


  return (
    <ToolContainer>
        <div className="max-w-md mx-auto space-y-4">
          <div><Label>Landlord Name *</Label><Input aria-label="Landlord Name" value={landlord} onChange={e => setLandlord(e.target.value)} /></div>
          <div><Label>Tenant Name *</Label><Input aria-label="Tenant Name" value={tenant} onChange={e => setTenant(e.target.value)} /></div>
          <div><Label>Property Address *</Label><Input aria-label="Property Address" value={address} onChange={e => setAddress(e.target.value)} /></div>
          <div>
            <Label>Notice Type</Label>
            <Select value={noticeType} onValueChange={(v) => chooseType(v as NoticeType)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {NOTICE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {noticeType === "pay-or-quit" && (
            <div><Label htmlFor="ev-amount">Amount Owed ($) *</Label><Input id="ev-amount" type="number" inputMode="decimal" min={0} step="0.01" aria-invalid={!!amountProblem} value={amountOwed} onChange={e => setAmountOwed(e.target.value)} />{amountProblem && <p role="alert" className="text-xs text-destructive mt-1">{amountProblem}</p>}</div>
          )}
          {noticeType === "cure-or-quit" && (
            <div><Label>What is the violation? *</Label><Input aria-label="What is the violation?" value={violation} onChange={e => setViolation(e.target.value)} placeholder="e.g. Unauthorized pet in the unit" /></div>
          )}
          <div>
            <Label>State *</Label>
            <select className="border rounded px-2 py-2 w-full bg-background text-base sm:text-sm" aria-label="State" value={state} onChange={e => chooseState(e.target.value)}>
              <option value="">Choose a state</option>
              {US_STATES.map(st => <option key={st} value={st}>{st}</option>)}
            </select>
          </div>
          <div>
            <Label>Days to Comply *</Label>
            <Input aria-label="Days to Comply" type="number" min={1} value={daysToComply} onChange={e => setDaysToComply(e.target.value)} data-testid="ev-days" />
            {periodProblem && <p role="alert" data-testid="ev-period-error" className="text-xs text-red-600 mt-1">{periodProblem}</p>}
            {state && noticeType === "pay-or-quit" && !known && (
              <NoticeBox className="text-xs mt-1" testId="ev-unknown-state">We don't have a verified minimum notice period for {state}. Check your state's statute before serving this notice.</NoticeBox>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Notice rules vary by state and city. Built-in minimums (pay-or-quit): {statesWithKnownMinimums().join(", ")}. Some properties (for example federally backed housing covered by the CARES Act) must give 30 days' notice for unpaid rent. This note is for you and is not printed on the notice.
          </p>
          {missingRequired && (
            <NoticeBox className="text-xs">Fields marked * are required before you can preview or download.</NoticeBox>
          )}

          <Button variant="outline" onClick={() => setShowPreview(v => !v)} disabled={missingRequired} className="w-full gap-2">
            {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />} {showPreview ? "Hide Preview" : "Preview"}
          </Button>

          {showPreview && !missingRequired && (
            <LegalDocPreview title={TITLE} sections={buildSections()} />
          )}

          <div className="flex gap-2">
            <Button onClick={async () => { const allowed = await logUsageNow(); if (!allowed) return; generateLegalDoc(TITLE, buildSections(), "eviction-notice.docx"); }} disabled={missingRequired} className="flex-1 gap-2">
              <Download className="h-4 w-4" /> Word
            </Button>
            <Button onClick={async () => { const allowed = await logUsageNow(); if (!allowed) return; generateLegalDocPdf(TITLE, buildSections(), "eviction-notice.pdf"); }} disabled={missingRequired} variant="secondary" className="flex-1 gap-2">
              <Download className="h-4 w-4" /> PDF
            </Button>
          </div>
        </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} disclaimer="This tool generates a general-purpose eviction notice template. It isn't legal advice and may not account for the specific laws of your state, country, or situation — requirements for this type of eviction notice can vary significantly by jurisdiction. Have any document reviewed by a qualified attorney before you sign it or rely on it, especially for anything involving significant money, property, or legal obligations." />
    </ToolContainer>
  );
}
